import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff } from "lucide-react";
import { z } from "zod";
import { env } from "@/env";
import { useBoomerForm } from "@/form/boomer/hook";
import { useValidator } from "@/form/common/validator";
import { AddressAutocomplete } from "@/form/components/address-autocomplete";
import { CustomFieldAvatar } from "@/form/components/custom-field-avatar";
import { CustomFieldFile } from "@/form/components/custom-field-file";
import { CustomFieldInput } from "@/form/components/custom-field-input";
import { CustomFieldMultiSelect } from "@/form/components/custom-field-multi-select";
import { CustomFieldSelect } from "@/form/components/custom-field-select";
import { CustomFieldToggle } from "@/form/components/custom-field-toggle";
import type {
  AcceptEmployeeInviteInput,
  CustomFieldDefinition,
  Invitation,
  ValueOption,
} from "@/lib/backend/api";
import {
  hasPendingFileRead,
  isEmptyCustomFieldValue,
  isPendingFileValue,
} from "@/lib/custom-fields/build-value-input";
import { Routes } from "@/lib/router";
import { logger } from "@/logger";

export type AcceptEmployeeInviteFormProps = {
  id?: string;
  onSubmit: (values: AcceptEmployeeInviteInput) => Promise<void>;
  disabled?: boolean;
  invitation?: Invitation;
};

export function AcceptInviteEmployeeForm({
  disabled,
  invitation,
  onSubmit,
}: AcceptEmployeeInviteFormProps) {
  const { t } = useTranslation();
  const params = Routes.AuthAcceptInvite.useParams();
  const token = params.token ?? "";
  const validator = useValidator();
  const passwordMinLength = env.VITE_PASSWORD_MIN_LENGTH;
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);

  // Refs to hold field APIs for programmatic updates
  const zipCodeFieldRef = useRef<any>(null);
  const cityFieldRef = useRef<any>(null);
  const stateFieldRef = useRef<any>(null);

  // Get registration custom fields from invitation (already fetched by parent)
  // TODO: The backend API needs to be updated to include registrationFields in the Invitation type
  // @ts-expect-error - registrationFields is not in the Invitation type yet
  const customFieldDefinitions: CustomFieldDefinition[] = invitation?.registrationFields ?? [];

  // Build default values including custom fields
  const defaultValues: Record<string, any> = {
    firstname: invitation?.firstname ?? "",
    lastname: invitation?.lastname ?? "",
    token,
    password: "",
    repeatPassword: "",
    phone: "+31",
    dayOfBirth: "",
    image: "",
    addressLine: "",
    city: "",
    zipCode: "",
    countryCode: "NL",
    state: "",
  };

  // Add default values for custom fields
  customFieldDefinitions.forEach((fieldDef: CustomFieldDefinition) => {
    switch (fieldDef.type) {
      case "MULTI_SELECT":
        defaultValues[fieldDef.id] = [];
        break;
      case "BOOLEAN":
        defaultValues[fieldDef.id] = false;
        break;
      case "NUMBER":
        defaultValues[fieldDef.id] = null;
        break;
      default:
        defaultValues[fieldDef.id] = "";
    }
  });

  const [form, { Element: FormElement }] = useBoomerForm({
    defaultValues,
    validators: {
      onSubmit: ({ value }) => {
        // Log field names only: `value` carries the base64 content of any FILE field (#117).
        logger.info("Form validation started", { fieldNames: Object.keys(value as object) });

        if (!value.token) {
          logger.warn("Validation failed: missing token");
          return {
            form: "Token is required",
            fields: {
              token: "Required",
            },
          };
        }
        if (value.password !== value.repeatPassword) {
          logger.warn("Validation failed: passwords don't match");
          return {
            form: t("fields:repeatPassword.error.not-match"),
            fields: {
              repeatPassword: t("fields:repeatPassword.error.not-match"),
            },
          };
        }

        // Validate required custom fields
        if (customFieldDefinitions.length > 0) {
          const formValues = value as any;
          let hasValidationErrors = false;
          const fieldErrors: Record<string, string> = {};

          customFieldDefinitions.forEach((fieldDef: CustomFieldDefinition) => {
            const fieldValue = formValues[fieldDef.id];

            // A file that is still being read is not a sendable value: it would be skipped from
            // the payload below without any message, so block on it here (#117).
            if (isPendingFileValue(fieldDef.type, fieldValue)) {
              hasValidationErrors = true;
              fieldErrors[fieldDef.id] = t("common:applications.file-still-processing");
              return;
            }

            if (fieldDef.required && isEmptyCustomFieldValue(fieldDef.type, fieldValue)) {
              hasValidationErrors = true;
              fieldErrors[fieldDef.id] = t("common:validation.required");
            }
          });

          if (hasValidationErrors) {
            logger.warn("Validation failed: custom fields", { fieldErrors });
            return {
              form: t("common:applications.fill-required"),
              fields: fieldErrors,
            };
          }
        }

        logger.info("Form validation passed - returning undefined to allow submission");
        return undefined;
      },
    },
    onSubmit: async ({ value }) => {
      try {
        // Same reason as above: `value` carries the base64 content of any FILE field (#117).
        logger.info("Form onSubmit handler called", { fieldNames: Object.keys(value as object) });

        const formValues = value as any;

        // Build custom fields payload
        const customFieldsPayload: Record<string, object> = {};

        if (customFieldDefinitions.length > 0) {
          customFieldDefinitions.forEach((fieldDef: CustomFieldDefinition) => {
            const fieldValue = formValues[fieldDef.id];

            // Skip empty optional fields
            if (
              !fieldDef.required &&
              (fieldValue === null ||
                fieldValue === undefined ||
                fieldValue === "" ||
                (Array.isArray(fieldValue) && fieldValue.length === 0))
            ) {
              return;
            }

            // Build the custom field value object based on type
            switch (fieldDef.type) {
              case "TEXT":
                customFieldsPayload[fieldDef.id] = { text: fieldValue };
                break;
              case "NUMBER":
                // Handle number: null, undefined, or empty string becomes null, otherwise convert to number
                // This properly handles 0 as a valid value
                customFieldsPayload[fieldDef.id] = {
                  number:
                    fieldValue === null || fieldValue === undefined || fieldValue === ""
                      ? null
                      : Number(fieldValue),
                };
                break;
              case "DATE":
                customFieldsPayload[fieldDef.id] = { date: fieldValue };
                break;
              case "SELECT":
                customFieldsPayload[fieldDef.id] = { select: fieldValue };
                break;
              case "MULTI_SELECT":
                customFieldsPayload[fieldDef.id] = { multiSelect: fieldValue };
                break;
              case "BOOLEAN":
                customFieldsPayload[fieldDef.id] = { boolean: fieldValue };
                break;
              case "FILE":
                // Send FILE as base64 string (converted by CustomFieldFile component with base64=true)
                if (fieldValue && typeof fieldValue === "string") {
                  customFieldsPayload[fieldDef.id] = { file: fieldValue };
                } else {
                  logger.info("Skipping empty FILE field", { fieldId: fieldDef.id });
                }
                break;
              default:
                logger.warn("Unknown custom field type", {
                  type: fieldDef.type,
                  fieldId: fieldDef.id,
                });
            }
          });
        }

        // Build the final payload
        // TODO: The backend API needs to be updated to support customFields in AcceptEmployeeInviteInput
        const payload: AcceptEmployeeInviteInput & { customFields?: Record<string, object> } = {
          firstname: formValues.firstname,
          lastname: formValues.lastname,
          token: formValues.token,
          password: formValues.password,
          phone: formValues.phone, // Required field
          dayOfBirth: formValues.dayOfBirth, // Required field
          addressLine: formValues.addressLine, // Required field
          city: formValues.city, // Required field
          state: formValues.state, // Required field
          zipCode: formValues.zipCode, // Required field
          countryCode: formValues.countryCode || "NL", // Default to NL
          image: formValues.image || undefined, // Optional field
          customFields:
            Object.keys(customFieldsPayload).length > 0 ? customFieldsPayload : undefined,
        };

        // Never log the payload itself: a FILE custom field holds the full base64 content of a
        // personal document, which would land in the device log at the default log level (#117).
        logger.info("Submitting invite acceptance", {
          hasCustomFields: !!payload.customFields,
          customFieldCount: payload.customFields ? Object.keys(payload.customFields).length : 0,
        });

        await onSubmit(payload);
        logger.info("Form submission completed successfully");
      } catch (error) {
        logger.error("Form submission failed", { error });
        // Re-throw to let TanStack Form handle it
        throw error;
      }
    },
  });

  return (
    <FormElement className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        {/* Profile Photo */}
        <form.AppField
          name="image"
          validators={{
            onSubmit: z.string().min(1, t("fields:profile-image.error.required")),
          }}
          children={(field) => (
            <CustomFieldAvatar field={field} label="Profielfoto" required={true} />
          )}
        />

        <form.AppField
          name="firstname"
          validators={{
            onChange: validator.required({ name: "firstname" }),
            onBlur: validator.required({ name: "firstname" }),
            onSubmit: validator.required({ name: "firstname" }),
          }}
          children={(field) => (
            <field.InputField required={true} disabled={disabled} withPlaceholder />
          )}
        />
        <form.AppField
          name="lastname"
          validators={{
            onChange: validator.required({ name: "lastname" }),
            onBlur: validator.required({ name: "lastname" }),
            onSubmit: validator.required({ name: "lastname" }),
          }}
          children={(field) => <field.InputField required disabled={disabled} withPlaceholder />}
        />
        <form.AppField
          name="password"
          validators={{
            onChange: validator.password({ minLength: passwordMinLength }),
            onBlur: validator.password({ minLength: passwordMinLength }),
            onSubmit: validator.password({ minLength: passwordMinLength }),
          }}
          children={(field) => (
            <div className="relative flex items-center">
              <field.InputField
                type={showPassword ? "text" : "password"}
                autocomplete="new-password"
                required
                disabled={disabled}
                withPlaceholder
                helperText={t("fields:password.helperText", { number: passwordMinLength })}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={t(
                  showPassword ? "common:a11y.hide-password" : "common:a11y.show-password",
                )}
                className="absolute right-3 top-[40px] z-10 text-brand-900 hover:text-brand-900"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          )}
        />
        <form.AppField
          name="repeatPassword"
          validators={{
            onChange: validator.required({ name: "repeatPassword" }),
            onBlur: validator.required({ name: "repeatPassword" }),
            onSubmit: validator.required({ name: "repeatPassword" }),
          }}
          children={(field) => (
            <div className="relative flex items-center">
              <field.InputField
                type={showRepeatPassword ? "text" : "password"}
                autocomplete="new-password"
                required
                disabled={disabled}
                withPlaceholder
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowRepeatPassword(!showRepeatPassword)}
                aria-label={t(
                  showRepeatPassword ? "common:a11y.hide-password" : "common:a11y.show-password",
                )}
                className="absolute right-3 top-[40px] z-10 text-brand-900 hover:text-brand-900"
                tabIndex={-1}
              >
                {showRepeatPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          )}
        />
        <form.AppField
          name="phone"
          validators={{
            onChange: validator.phone({ schema: validator.required({ name: "phone" }) }),
            onBlur: validator.phone({ schema: validator.required({ name: "phone" }) }),
            onSubmit: validator.phone({ schema: validator.required({ name: "phone" }) }),
          }}
          children={(field) => (
            <field.PhoneField required disabled={disabled} defaultCountry="NL" />
          )}
        />

        <form.AppField
          name="dayOfBirth"
          validators={{
            onChange: validator.required({ name: "dayOfBirth" }),
            onBlur: validator.required({ name: "dayOfBirth" }),
            onSubmit: validator.required({ name: "dayOfBirth" }),
          }}
          children={(field) => (
            <field.InputField type="date" required disabled={disabled} withPlaceholder />
          )}
        />

        {/* Address fields with autocomplete */}
        <form.AppField
          name="addressLine"
          validators={{
            onChange: validator.required({ name: "addressLine" }),
            onBlur: validator.required({ name: "addressLine" }),
            onSubmit: validator.required({ name: "addressLine" }),
          }}
          children={(field: any) => (
            <AddressAutocomplete
              field={field}
              label="Adres"
              required
              onAddressSelect={(address) => {
                logger.info("onAddressSelect callback called", { address });

                // Update addressLine is already handled by AddressAutocomplete component
                // Update other address fields using their field refs
                if (zipCodeFieldRef.current) {
                  zipCodeFieldRef.current.handleChange(address.zipCode);
                }
                if (cityFieldRef.current) {
                  cityFieldRef.current.handleChange(address.city);
                }
                if (stateFieldRef.current) {
                  stateFieldRef.current.handleChange(address.state);
                }

                logger.info("All address fields updated", {
                  addressLine: address.addressLine,
                  zipCode: address.zipCode,
                  city: address.city,
                  state: address.state,
                });
              }}
            />
          )}
        />

        <form.AppField
          name="zipCode"
          validators={{
            onChange: validator.required({ name: "zipCode" }),
            onBlur: validator.required({ name: "zipCode" }),
            onSubmit: validator.required({ name: "zipCode" }),
          }}
          children={(field: any) => {
            zipCodeFieldRef.current = field;
            return <CustomFieldInput field={field} label="Postcode" required disabled={disabled} />;
          }}
        />

        <form.AppField
          name="city"
          validators={{
            onChange: validator.required({ name: "city" }),
            onBlur: validator.required({ name: "city" }),
            onSubmit: validator.required({ name: "city" }),
          }}
          children={(field: any) => {
            cityFieldRef.current = field;
            return <CustomFieldInput field={field} label="Stad" required disabled={disabled} />;
          }}
        />

        <form.AppField
          name="state"
          validators={{
            onChange: validator.required({ name: "state" }),
            onBlur: validator.required({ name: "state" }),
            onSubmit: validator.required({ name: "state" }),
          }}
          children={(field: any) => {
            stateFieldRef.current = field;
            return (
              <CustomFieldInput field={field} label="Provincie" required disabled={disabled} />
            );
          }}
        />

        {/* Registration custom fields */}
        {customFieldDefinitions.map((fieldDef: CustomFieldDefinition) => {
          const { id, name, type, required, options, description } = fieldDef;

          switch (type) {
            case "TEXT":
              return (
                <form.Field
                  key={id}
                  name={id as any}
                  validators={{
                    onChange: ({ value }: any) => {
                      if (required && !value) {
                        return t("common:validation.required");
                      }
                      return undefined;
                    },
                  }}
                  children={(field: any) => (
                    <CustomFieldInput
                      field={field}
                      label={name}
                      description={description}
                      required={required}
                    />
                  )}
                />
              );

            case "NUMBER":
              return (
                <form.Field
                  key={id}
                  name={id as any}
                  validators={{
                    onChange: ({ value }: any) => {
                      // Only check for null/undefined, not empty string
                      // 0 is a valid number value
                      if (required && (value === null || value === undefined)) {
                        return t("common:validation.required");
                      }
                      return undefined;
                    },
                  }}
                  children={(field: any) => (
                    <CustomFieldInput
                      field={field}
                      label={name}
                      description={description}
                      type="number"
                      required={required}
                    />
                  )}
                />
              );

            case "DATE":
              return (
                <form.Field
                  key={id}
                  name={id as any}
                  validators={{
                    onChange: ({ value }: any) => {
                      if (required && !value) {
                        return t("common:validation.required");
                      }
                      return undefined;
                    },
                  }}
                  children={(field: any) => (
                    <CustomFieldInput
                      field={field}
                      label={name}
                      description={description}
                      type="date"
                      required={required}
                    />
                  )}
                />
              );

            case "SELECT":
              return (
                <form.Field
                  key={id}
                  name={id as any}
                  validators={{
                    onChange: ({ value }: any) => {
                      if (required && !value) {
                        return t("common:validation.required");
                      }
                      return undefined;
                    },
                  }}
                  children={(field: any) => (
                    <CustomFieldSelect
                      field={field}
                      label={name}
                      description={description}
                      required={required}
                      options={
                        options?.map((opt: ValueOption) => ({
                          label: opt.label,
                          value: opt.value,
                        })) ?? []
                      }
                    />
                  )}
                />
              );

            case "MULTI_SELECT":
              return (
                <form.Field
                  key={id}
                  name={id as any}
                  validators={{
                    onChange: ({ value }: any) => {
                      if (required && (!value || value.length === 0)) {
                        return t("common:validation.required");
                      }
                      return undefined;
                    },
                  }}
                  children={(field: any) => (
                    <CustomFieldMultiSelect
                      field={field}
                      label={name}
                      description={description}
                      required={required}
                      options={
                        options?.map((opt: ValueOption) => ({
                          label: opt.label,
                          value: opt.value,
                        })) ?? []
                      }
                    />
                  )}
                />
              );

            case "BOOLEAN":
              return (
                <form.Field
                  key={id}
                  name={id as any}
                  validators={{
                    onChange: ({ value }: any) => {
                      if (required && value !== true && value !== false) {
                        return t("common:validation.required");
                      }
                      return undefined;
                    },
                  }}
                  children={(field: any) => (
                    <CustomFieldToggle
                      field={field}
                      label={name}
                      description={description}
                      required={required}
                    />
                  )}
                />
              );

            case "FILE":
              return (
                <form.Field
                  key={id}
                  name={id as any}
                  validators={{
                    onChange: ({ value }: any) => {
                      // Right after picking a file the value is still a raw File while it is being
                      // read. Don't flash "verplicht" then; the form validator blocks on it (#117).
                      if (isPendingFileValue("FILE", value)) {
                        return undefined;
                      }
                      if (required && isEmptyCustomFieldValue("FILE", value)) {
                        return t("common:validation.required");
                      }
                      return undefined;
                    },
                  }}
                  children={(field: any) => (
                    <CustomFieldFile
                      field={field}
                      label={name}
                      description={description}
                      required={required}
                      base64={true}
                    />
                  )}
                />
              );

            default:
              logger.warn("Unknown custom field type", { type, fieldId: id });
              return null;
          }
        })}
      </div>

      {/* `canSubmit` stays true while a chosen file is still being read: the FILE validator returns
          undefined in that window on purpose, so it does not flash "verplicht" at someone who just
          picked a photo. That leaves the button clickable during the read, and the only thing
          stopping you was an error afterwards. Ask for the read directly instead (#117). */}
      <form.Subscribe
        selector={(state) =>
          [state.canSubmit, state.isSubmitting, hasPendingFileRead(state.values as never)] as const
        }
        children={([canSubmit, isSubmitting, isReadingFile]) => (
          <button
            type="submit"
            disabled={!canSubmit || isSubmitting || isReadingFile}
            className="hover:bg-button-primary/90 w-full rounded-lg bg-button-primary px-4 py-3 text-base font-medium text-txt-inverted transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isReadingFile ? t("fields:file.processing") : t("common:save")}
          </button>
        )}
      />
    </FormElement>
  );
}
